import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import OldLoading from '@picsart/rc/old/Loading';
import Loader from '@picsart/rc/Loader';
import findLastIndex from 'lodash/findLastIndex';
import Skeleton from '@picsart/rc/old/Skeleton';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import noop from '@picsart/rc/noop';
import { ZOOM_MAX, ZOOM_MIN } from '@picsart/web-layering/configs';
import View from '@picsart/view/View';

import { TAnyObject, MaskType } from 'types';
import useLoadingImage from 'hooks/useLoadingImage';
import useToolUndoRedo from 'hooks/useToolUndoRedo';
import useMemoSelector from 'hooks/useMemoSelector';
import usePremiumToaster from 'hooks/usePremiumToaster';
import useEventListener from 'hooks/common/useEventListener';
import useMount from 'hooks/common/useMount';
import UndoRedo from 'components/common/UndoRedo';
import OldUndoRedo from 'components/common/OldUndoRedo';
import {
  isDev,
  Messages,
  zoomData,
  ToolTypes,
  zoomValues,
  ZOOM_INPUT_CHANGE_VALUE,
  ZOOM_INPUT_DOUBLE_STEP_VALUE,
} from 'configs/constants';
import FormDropdown from 'components/common/Dropdown';
import useMountedRef from 'hooks/common/useMountedRef';
import withAnimationFrame from 'utils/withAnimationFrame';
import ZoomInOut from 'components/common/ZoomInOut';
import { isEnterEvent, isMetaKey, isZoomInEvent, isZoomOutEvent } from 'utils/common/hotKeyCombinations';
import analytics from 'services/analytics';
import ANALYTICS_EVENTS from 'services/analytics/config';
import { getUserData } from 'store/selectors/general';
import useSubscribe from 'hooks/useSubscribe';

import useStyles from './styles';
import EraserStage from './EraserStage';
import EraserHeader from './EraserHeader';
import EraserSidebar from './EraserSidebar';
import EraserSettings, { SettingDataType } from './EraserSettings';
import { cutoutImage, getDefaultSettings, invertMask } from './utils';
import { EMPTY_IMAGE, EraserCutoutEnum, EraserHistoryToolsEnum, EraserToolsEnum, IEraserImageAttrs, INVERT_URL, IS_BRUSH_USED, IS_ERASER_USED } from './config';

export type CutoutType = 'object' | 'person' | '';

interface IEraserBrushProps {
  width: number;
  height: number;
  onCancel: (rmObjectBgApplied: boolean) => void;
  defaultMask?: MaskType;
  cutOutKeepMask?: boolean;
  isPremiumImage?: boolean;
  hasPersonCutout?: boolean;
  onMaskChange?: (cutoutType: string | null) => void;
  isRmObjectApplied?: boolean;
  defaultActiveTool?: EraserToolsEnum;
  originalImageAttrs?: IEraserImageAttrs;
  isPassedImageHSLApplied?: boolean;
  effectImage: HTMLImageElement | undefined;
  originalImage: HTMLImageElement | undefined;
  onBgRemover?: (cutoutType: CutoutType, mask?: MaskType) => void;
  onApply: (mask: MaskType, analytics: { mode?: EraserHistoryToolsEnum; settings: SettingDataType }) => void;
  isDarkMode?: boolean;
}

const EraserBrush = ({
  width,
  height,
  onApply,
  onCancel,
  defaultMask,
  effectImage,
  originalImage,
  originalImageAttrs,
  onBgRemover = noop,
  onMaskChange = noop,
  cutOutKeepMask = true,
  hasPersonCutout = true,
  isPremiumImage = false,
  isRmObjectApplied = false,
  isPassedImageHSLApplied = false,
  defaultActiveTool = EraserToolsEnum.ERASER,
  isDarkMode = false,
}: IEraserBrushProps) => {
  const isMountedRef = useMountedRef();
  const {
    isAuthorized,
    isSubscribed,
  } = useMemoSelector(getUserData);
  const {
    subscribe,
  } = useSubscribe();
  const showToaster = usePremiumToaster();
  const emptyMask = useLoadingImage(EMPTY_IMAGE);
  const emptyInvertMask = useLoadingImage(INVERT_URL);
  const containerRef = useEventListener<HTMLDivElement>('contextmenu', (e: Event) => e.preventDefault());
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const eraserSidebarRef = useRef<HTMLDivElement>(null);
  const stageWrapperRef = useRef<HTMLDivElement>(null);
  const isGestureCanceled = useRef<boolean>(false);
  const gestureEventQueue = useRef<number[]>([]);
  const analyticsRef = useRef<Record<string, Record<string, string | boolean>>>({
    eraser: {
      name: IS_ERASER_USED,
      value: false,
    },
    brush: {
      name: IS_BRUSH_USED,
      value: false,
    },
  });

  const initialMask = defaultMask || emptyMask;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [mask, setMask] = useState<MaskType>();
  const [saveActive, setSaveActive] = useState<boolean>(false);
  const [activeTool, setActiveTool] = useState<EraserToolsEnum>(defaultActiveTool);
  const [activeMode, setActiveMode] = useState<EraserHistoryToolsEnum | undefined>();
  const [settings, setSettings] = useState<SettingDataType>(() => getDefaultSettings());
  const [maxWidthCanvas, setMaxWidthCanvas] = useState<number>(0);

  const [zoom, setZoom] = useState(() => zoomData.find(item => item.label === '100%') as TAnyObject);
  const [localZoomValue, setLocalZoomValue] = useState<number>(1);

  const showLoading = useMemo(() => loading, [loading]);
  const isZoomed = zoom.value > 1 || zoom.value === zoomValues.fill.value;

  const classes = useStyles({
    isDarkMode,
    isZoomed,
  });

  const handleHistoryChange = useCallback(({ lineImage }, step: number) => {
    if (step < 0) {
      setActiveTool(EraserToolsEnum.ERASER);
    }
    setSaveActive(step >= 0);
    setMask(lineImage);
  }, []);

  const mountEraserStage = useMemo(
    () => originalImage && effectImage && mask,
    [originalImage, effectImage, mask],
  );

  const [
    history, historyStep, historyDisabled, addEraserHistory, undoRedoHandler,
  ] = useToolUndoRedo(handleHistoryChange, initialMask, !!mountEraserStage && !showLoading);

  const historyItem = useMemo(() => history[historyStep], [history, historyStep]);
  const actualHistory = useMemo(() => history.slice(0, historyStep + 1), [history, historyStep]);

  const lastRemovalTool = useMemo(() => {
    const resetLastIndex = findLastIndex(actualHistory, item => !!item?.isReset);
    const rmLastIndex = findLastIndex(actualHistory, item => !!item?.appliedRemoverTool);

    if (resetLastIndex > rmLastIndex) {
      return null;
    }

    return actualHistory[rmLastIndex]?.appliedRemoverTool;
  }, [actualHistory]);

  const objectRmApplied = useMemo(() => lastRemovalTool === 'object', [lastRemovalTool]);

  const premiumText = useMemo(() => {
    if (!objectRmApplied && !isRmObjectApplied && !isPassedImageHSLApplied && isPremiumImage) {
      return 'This is a premium photo';
    }
    if (objectRmApplied || isRmObjectApplied || isPassedImageHSLApplied) {
      return Messages.PREMIUM_LAYER;
    }
    return null;
  }, [objectRmApplied, isRmObjectApplied, isPassedImageHSLApplied, isPremiumImage]);

  const isPremium = useMemo(() => !isSubscribed && !!premiumText, [premiumText, isSubscribed]);

  useEffect(() => {
    objectRmApplied && showToaster();
  }, [objectRmApplied, showToaster]);

  useEffect(() => {
    setInitialLoading(!originalImage || !effectImage || !mask);
  }, [effectImage, originalImage, mask]);

  useEffect(() => {
    setMask(initialMask);
  }, [initialMask]);

  const handleChange = useCallback(() => {
    setSaveActive(true);
  }, []);

  const handleMaskChange = useCallback((
    newMask: MaskType | undefined,
    cutoutType: string | null = null,
    isReset: boolean = false,
  ) => {
    if (!isDarkMode && activeTool !== EraserToolsEnum.CUT_OUT && !analyticsRef.current[activeTool].value) {
      analyticsRef.current[activeTool].value = true;
    }

    setMask(newMask);
    newMask && addEraserHistory({
      lineImage: newMask,
      isReset,
    }, cutoutType);
    onMaskChange(cutoutType);
  }, [activeTool, addEraserHistory, isDarkMode, onMaskChange]);

  const handleReset = useCallback(async () => {
    if (historyItem?.isReset || historyStep === -1) {
      return;
    }

    handleMaskChange(initialMask, null, true);
    setActiveMode(EraserHistoryToolsEnum.RESET);
    setSaveActive(false);
  }, [handleMaskChange, historyItem, historyStep, initialMask]);

  const handleInvert = useCallback(() => {
    const invertedMask = invertMask(mask, originalImage, width, height);
    invertedMask && handleMaskChange(invertedMask);
    setActiveMode(EraserHistoryToolsEnum.INVERT);
    setSaveActive(true);
  }, [height, mask, originalImage, width, handleMaskChange]);

  const handleCutoutImage = useCallback(async (cutoutType: CutoutType = '', isDeeplink: boolean = false) => {
    if ((!isAuthorized && !isDeeplink) && !isDev) {
      return subscribe();
    }

    cutoutType === EraserCutoutEnum.Object && analytics.sendEvent(ANALYTICS_EVENTS.PHOTO_EDIT_ITEM_OPEN, {
      item: ToolTypes.removeObjectBg,
      origin: 'tool_erase',
      is_premium: true,
      source: 'tool_erase',
    });

    setLoading(true);
    const image = cutOutKeepMask ? originalImage : effectImage;
    const personMask = await cutoutImage(mask as MaskType, image, width, height, cutOutKeepMask, cutoutType);
    const newMask = cutOutKeepMask ? personMask : invertMask(personMask, emptyInvertMask, width, height);
    handleMaskChange(newMask, cutoutType);
    setSaveActive(true);
    setLoading(false);
    onBgRemover(cutoutType, newMask);
  }, [isAuthorized, cutOutKeepMask, originalImage, effectImage, mask, width, height, emptyInvertMask, handleMaskChange, onBgRemover, subscribe]);

  const onApplyPress = useCallback(() => {
    if (!mask) return;

    setLoading(true);
    onApply(mask, { mode: activeMode, settings });
  }, [mask, onApply, activeMode, settings]);

  const handleCancel = useCallback(() => {
    onCancel(objectRmApplied);
  }, [objectRmApplied, onCancel]);

  const handleChangeZoom = useCallback(({ id, label, value }: TAnyObject) => {
    if (zoom.id === id) {
      return;
    }

    setZoom({
      id,
      label,
      value,
    });
  }, [zoom.id]);

  const calculateStageScale = useMemo(() => {
    if (!containerRef.current) {
      return 1;
    }

    const scale = zoom.value;

    let stageScale = (scale === zoomValues.fill.value ? 1 : scale) as number;
    const { offsetWidth } = containerRef.current;

    if (scale === zoomValues.fill.value || (offsetWidth < width * (scale as number) && scale <= 1)) {
      const zoomValue = (scale === zoomValues.fill.value ? 1 : scale) as number;
      stageScale = (offsetWidth / width) * zoomValue;
    }

    return stageScale;
  }, [containerRef, zoom.value, width]);

  const isShowHorizontalBar = width * calculateStageScale > maxWidthCanvas;

  const resizeObserver = new ResizeObserver(() => {
    withAnimationFrame(() => {
      if (!isMountedRef.current) return;
      setMaxWidthCanvas(
        (pageContainerRef.current?.offsetWidth || 0) - (eraserSidebarRef.current?.offsetWidth || 0),
      );
    });
  });

  if (stageWrapperRef && stageWrapperRef.current) {
    resizeObserver.unobserve(stageWrapperRef.current);
    resizeObserver.observe(stageWrapperRef.current);
  }

  useMount(() => {
    setMaxWidthCanvas((pageContainerRef.current?.offsetWidth || 0) - (eraserSidebarRef.current?.offsetWidth || 0));
  });

  const updateZoom = useCallback((zoomValue: number, exact: boolean = false) => {
    setLocalZoomValue(prevZoomValue => {
      const nextZoom = exact ? zoomValue : prevZoomValue + zoomValue;
      const nextZoomValue = Math.min(Math.max(nextZoom, ZOOM_MIN), ZOOM_MAX);

      setZoom({
        id: nextZoomValue,
        label: `${nextZoomValue * 100}%`,
        value: nextZoomValue,
      });

      return nextZoomValue;
    });
  }, []);

  const onZoomBlur = useCallback(() => {
    const nextZoomValue = Math.min(Math.max(localZoomValue, ZOOM_MIN), ZOOM_MAX);
    // store may not update on duplicated store change
    setLocalZoomValue(nextZoomValue);

    updateZoom(nextZoomValue, true);
  }, [localZoomValue, updateZoom]);

  const onZoomKeyPress = useCallback((event: any) => {
    isEnterEvent(event) && onZoomBlur();
  }, [onZoomBlur]);

  const onZoomChange = useCallback((nextZoom: number) => {
    if (Number.isNaN(nextZoom)) return;

    setLocalZoomValue(nextZoom);
  }, []);

  const onZoomIn = useCallback(() => {
    updateZoom(0.25);
  }, [updateZoom]);

  const onZoomOut = useCallback(() => {
    updateZoom(-0.25);
  }, [updateZoom]);

  useMount(() => {
    if (!isDarkMode) return;

    const keydownHandler = (event: KeyboardEvent) => {
      if (isMetaKey(event) && (isZoomInEvent(event) || isZoomOutEvent(event))) {
        event.preventDefault();
        event.stopPropagation();
        updateZoom(isZoomInEvent(event) ? ZOOM_INPUT_CHANGE_VALUE : -ZOOM_INPUT_CHANGE_VALUE);
      }
    };

    const wheelHandler = (event: WheelEvent) => {
      if (containerRef.current?.contains(event.target as Node) && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
        if (event.deltaY < 0) {
          updateZoom(ZOOM_INPUT_DOUBLE_STEP_VALUE);
        } else if (event.deltaY > 0) {
          updateZoom(-ZOOM_INPUT_DOUBLE_STEP_VALUE);
        }
      }
    };

    const safariGestureEnd = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();

      isGestureCanceled.current = false;
    };

    const safariGestureStart = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();

      isGestureCanceled.current = true;
    };

    const safariGestureChange = (event: any) => {
      if (containerRef.current?.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();

        gestureEventQueue.current.push(setTimeout(() => {
          if (isGestureCanceled.current) {
            updateZoom(event.scale > 1 ? ZOOM_INPUT_DOUBLE_STEP_VALUE : -ZOOM_INPUT_DOUBLE_STEP_VALUE);
          } else {
            gestureEventQueue.current.forEach(clearTimeout);
          }
        }) as unknown as number);
      }
    };

    document.addEventListener('keydown', keydownHandler, true);
    document.addEventListener('gestureend', safariGestureEnd, true);
    document.addEventListener('gesturestart', safariGestureStart, true);
    document.addEventListener('gesturechange', safariGestureChange, true);
    document.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

    return () => {
      document.removeEventListener('wheel', wheelHandler, true);
      document.removeEventListener('keydown', keydownHandler, true);
      document.removeEventListener('gestureend', safariGestureEnd, true);
      document.removeEventListener('gesturestart', safariGestureStart, true);
      document.removeEventListener('gesturechange', safariGestureChange, true);
    };
  });

  return (
    <div className={classes.EraserPage}>
      <EraserHeader
        onCancel={handleCancel}
        saveActive={saveActive}
        onApply={onApplyPress}
        loading={showLoading || initialLoading}
        isDarkMode={isDarkMode}
      />
      <div ref={pageContainerRef} className={classes.container}>
        <EraserSidebar
          onReset={handleReset}
          onInvert={handleInvert}
          activeTool={activeTool}
          onToolSelect={setActiveTool}
          hasPersonCutout={hasPersonCutout}
          brushDisabled={!saveActive && !defaultMask}
          sidebarContainerRef={eraserSidebarRef}
          isLoading={showLoading || initialLoading}
          isDisabled={saveActive}
          isDarkMode={isDarkMode}
        >
          <EraserSettings
            settings={settings}
            onSettingsChange={setSettings}
            activeTool={activeTool}
            onCutout={handleCutoutImage}
            appliedRemoverTool={lastRemovalTool}
            loading={showLoading || initialLoading}
            isDarkMode={isDarkMode}
          />
        </EraserSidebar>

        <div
          className={classNames(classes.eraserStageMain, {
            [classes.eraserStageMainZoomed]: isZoomed,
          })}
        >
          <div className={classes.eraserStageWrapper} ref={stageWrapperRef}>
            <div
              ref={containerRef}
              className={classNames(classes.eraserStageContainer, {
                [classes.eraserStageContainerCenter]: !isZoomed || !isShowHorizontalBar,
              })}
            >
              <div className={classes.eraserStageContainerVertical}>
                {mountEraserStage && (
                <EraserStage
                  mask={mask}
                  width={width}
                  height={height}
                  scale={calculateStageScale}
                  settings={settings}
                  activeTool={activeTool}
                  actionsIsBlock={showLoading}
                  onChange={handleChange}
                  effectImage={effectImage}
                  showWatermark={isPremium}
                  onToolSelect={setActiveTool}
                  originalImage={originalImage}
                  onMaskChange={handleMaskChange}
                  originalImageAttrs={originalImageAttrs}
                  containerRef={containerRef}
                  maxWidthCanvas={maxWidthCanvas}
                />
                )}
              </div>
            </div>
          </div>

          {mountEraserStage && (
          <div
            className={classNames(classes.canvasFooter, {
              [classes.canvasFooterContainer]: isDarkMode,
            })}
          >
            {!isDarkMode ? (
              <div className={classes.canvasFooterWrapper}>
                <OldUndoRedo
                  step={historyStep}
                  historyLength={history.length}
                  disabled={historyDisabled || loading}
                  onClickHandler={undoRedoHandler}
                />
                <FormDropdown
                  data={zoomData}
                  labelName="label"
                  valueName="value"
                  onChange={handleChangeZoom}
                  selected={zoom}
                  horizontalPosition="center"
                  verticalPositionType="top"
                  tooltip="Zoom"
                />
              </div>
            ) : (
              <>
                <View flex={1}>
                  <UndoRedo
                    step={historyStep}
                    historyLength={history.length}
                    disabled={historyDisabled || loading}
                    onClickHandler={undoRedoHandler}
                  />
                </View>
                <View flex={1} justifyContent="center">
                  <ZoomInOut
                    onZoomIn={onZoomIn}
                    onZoomOut={onZoomOut}
                    inputOnBlur={onZoomBlur}
                    inputOnChange={onZoomChange}
                    inputOnKeyPress={onZoomKeyPress}
                    inputValue={Math.round(localZoomValue * 100)}
                  />
                </View>
                <View flex={1} />
              </>
            )}
          </div>
          )}

          {showLoading && (
          <div className={classes.loaderContainer}>
            {!isDarkMode ? (
              <OldLoading className={classes.loaderIcon} />
            ) : (
              <Loader />
            ) }
          </div>
          )}

          {initialLoading && (
          <div className={classes.skeletonContainer}>
            <Skeleton />
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EraserBrush;
